import { GlobalKeysContext } from '@bonsai-components/react-global-keys';

import { type KeyboardEvent, useContext, useEffect } from 'react';
import {
  CommandPaletteContext,
  type CommandPaletteItem,
  type CommandWithKeyBinding
} from '../contexts/command-palette.context';

const EXCLUDED_SELECTOR =
  'input, textarea, [role="textbox"],.CodeMirror-code,[data-no-kb-commands="true"],[data-no-kb-commands="true"] *';

export const excludeActionFromInputMode = (
  e: KeyboardEvent<Element>,
  // biome-ignore lint/suspicious/noConfusingVoidType: void is correct here — callers return void, not undefined
  action: (e: KeyboardEvent<Element>) => void | Promise<boolean>
) => {
  const target = e.target as HTMLElement;
  // Check if the target itself matches or is within an excluded element
  const isExcluded =
    target.matches(EXCLUDED_SELECTOR) ||
    target.closest(EXCLUDED_SELECTOR) !== null;

  if (isExcluded) {
    return;
  }
  return action(e);
};

/**
 * Composes addCommands from CommandPaletteContext and addKeyBinding from
 * GlobalKeysContext and returns a single function that can be called on a
 * command input or array of command inputs
 */
const useAddCommandsAndKeyBindings = () => {
  const { addCommands } = useContext(CommandPaletteContext);
  const { addKeyBinding } = useContext(GlobalKeysContext);

  return (command: CommandPaletteItem) => {
    addCommands({ ...command });
    if (command.keyboardShortcuts) {
      if (Array.isArray(command.keyboardShortcuts)) {
        command.keyboardShortcuts.forEach((kb) => {
          addKeyBinding({
            ...kb,
            action: (e) =>
              kb.includeInputMode
                ? command.action(e)
                : excludeActionFromInputMode(e, command.action),
            description: command.description || command.label
          });
        });
      } else {
        addKeyBinding({
          ...command.keyboardShortcuts,
          action: (e) =>
            (command.keyboardShortcuts as CommandWithKeyBinding)
              .includeInputMode
              ? command.action(e)
              : excludeActionFromInputMode(e, command.action),
          description: command.description || command.label
        });
      }
    }
  };
};

/**
 * Adds a command input or array of command inputs to the appropriate contexts
 * on mount.
 */
export const useCommandPalette = (
  commands: CommandPaletteItem | CommandPaletteItem[],
  deps: unknown[] = []
) => {
  const composedAddCommand = useAddCommandsAndKeyBindings();
  const { removeCommands } = useContext(CommandPaletteContext);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are handled by the caller
  useEffect(() => {
    if (Array.isArray(commands)) {
      commands.forEach(composedAddCommand);
    } else {
      composedAddCommand(commands);
    }
    // Remove commands on unmount to prevent page specific commands from leaking
    return () => {
      if (Array.isArray(commands)) {
        commands.forEach((command) => removeCommands(command.label));
      } else {
        removeCommands(commands.label);
      }
    };
  }, [...deps]);
};
